import { ActionIcon, Group, Tooltip } from '@mantine/core'
import { IconPencil, IconReload, IconTrash } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { CrudAction, Resource } from '@/types/permissions'
import { Pipeline, PipelineStatus } from '@/types/vlm'

const RESTARTABLE_STATUSES = [
  PipelineStatus.Running,
  PipelineStatus.Failed,
  PipelineStatus.Degraded,
  PipelineStatus.Stopped
]

type VlmPipelineListActionsProps = {
  pipeline: Pipeline
  hasVlmAgentsAccess: boolean
  onEdit: () => void
  onDelete: () => void
  onRestart: () => void
}

export const VlmPipelineListActions = ({
  pipeline,
  hasVlmAgentsAccess,
  onEdit,
  onDelete,
  onRestart
}: VlmPipelineListActionsProps) => {
  const intl = useIntl()
  const [canCreate] = useHasPermissions(Resource.VlmPipelines, [
    CrudAction.Create
  ])
  const [canDelete] = useHasPermissions(Resource.VlmPipelines, [
    CrudAction.Delete
  ])

  const showRestart =
    canCreate && RESTARTABLE_STATUSES.includes(pipeline.status)
  const isRestartDisabled = !hasVlmAgentsAccess
  const restartTooltipId = isRestartDisabled
    ? 'vlm.entitlement.disabledTooltip'
    : 'vlm.pipelines.actions.restart'

  return (
    <Group wrap="nowrap" gap={8} justify="end">
      {canCreate && (
        <Tooltip label={<FormattedMessage id="vlm.pipelines.actions.edit" />}>
          <ActionIcon
            variant="outline"
            size="md"
            radius="sm"
            color="blue"
            aria-label={intl.formatMessage({
              id: 'vlm.pipelines.actions.edit'
            })}
            onClick={onEdit}
          >
            <IconPencil style={{ width: '70%', height: '70%' }} />
          </ActionIcon>
        </Tooltip>
      )}

      {showRestart && (
        <Tooltip label={<FormattedMessage id={restartTooltipId} />}>
          <ActionIcon
            variant="outline"
            size="md"
            radius="sm"
            color="orange"
            disabled={isRestartDisabled}
            aria-label={intl.formatMessage({
              id: 'vlm.pipelines.actions.restart'
            })}
            onClick={onRestart}
          >
            <IconReload style={{ width: '70%', height: '70%' }} />
          </ActionIcon>
        </Tooltip>
      )}

      {canDelete && (
        <Tooltip label={<FormattedMessage id="vlm.pipelines.actions.delete" />}>
          <ActionIcon
            variant="outline"
            size="md"
            radius="sm"
            color="red"
            aria-label={intl.formatMessage({
              id: 'vlm.pipelines.actions.delete'
            })}
            onClick={onDelete}
          >
            <IconTrash style={{ width: '70%', height: '70%' }} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  )
}
